/**
 * Iter-155B.2 — Shared onboarding/empty-state surface.
 *
 * Drops into ANY empty state where the resolution is "upload a
 * script". Replaces the previous five different empty states that
 * each pointed users at "Settings → Script Setup" (jargon, requires
 * navigation knowledge).
 *
 * Three variants:
 *   - "welcome" → primary onboarding card on ProjectHome when the
 *     project is brand-new and has no scripts yet.
 *   - "scenes"  → empty ScenesHub / OnSetMode / PrepMode.
 *   - "cast"    → empty CastHub / BulkCastEdit.
 *
 * Single CTA: "Upload Script" — routes to /projects/{id}/script-setup
 * (a dedicated page with no Settings clutter). The button label and
 * sub-line stay constant; only the headline + 1-line message change
 * per variant so each empty surface still feels native.
 */
import { useNavigate, useLocation } from "react-router-dom";
import { FileText, Upload, Wand2, Image as ImageIcon } from "lucide-react";
import { useRouteBase } from "../context/ApiBaseContext";

const VARIANT_COPY = {
    welcome: {
        title: "Welcome to your project",
        body:
            "To get started: upload a script and we'll build the scenes & cast list for you. Your continuity (photos, notes, looks) lives on top of that.",
        steps: [
            { icon: Upload, label: "Upload script", tone: "primary" },
            { icon: Wand2, label: "Build Scenes & Cast", tone: "neutral" },
            { icon: ImageIcon, label: "Start adding continuity", tone: "neutral" },
        ],
        testid: "onboarding-welcome",
    },
    scenes: {
        title: "No scenes yet",
        body: "Upload a script to create scenes and cast.",
        steps: null,
        testid: "onboarding-scenes",
    },
    cast: {
        title: "No cast yet",
        body: "Upload a script to create cast members. You can also add cast manually later.",
        steps: null,
        testid: "onboarding-cast",
    },
    // Iter-158.13.2.20f.7 (BLOCKER 2) — H&M-shaped missing-script
    // empty state. H&M mirrors the Production's canonical script and
    // MUST NOT offer a Department-level upload workflow, even to a
    // Production Owner reaching H&M without a canonical script.
    // Authority-aware copy is layered on top of this variant by the
    // component below (the CTA is unchanged: route out to Production
    // Script Setup for authorised users; no CTA for department-only
    // users).
    "hm-missing-script": {
        title: "Waiting for the canonical script",
        body:
            "Hair & Makeup mirrors your Production's canonical script. Once your Production Owner or an authorised Production admin uploads it, scenes and cast will appear here.",
        steps: null,
        testid: "onboarding-hm-missing-script",
    },
};

export default function EmptyScriptOnboarding({
    projectId,
    variant = "welcome",
    secondaryAction = null, // { label, onClick }  optional
    className = "",
    role = null, // Iter-157D — gate Upload CTA by role.
    // Iter-158.13.2.20f.1 (#4) — authority signal for Route 2.
    // `project.can_manage_script` on the Hub GET is the Production
    // authority signal. On beta H&M mount an H&M-only admin's dept
    // role is "admin" but they must NOT see the Upload CTA — that
    // authority belongs to Production. Pass `canManageScript` from
    // the caller (falls back to legacy role check if omitted so
    // legacy behaviour is byte-identical).
    canManageScript = null,
}) {
    const navigate = useNavigate();
    const location = useLocation();
    const routeBase = useRouteBase();
    const copy = VARIANT_COPY[variant] || VARIANT_COPY.welcome;

    // Iter-157D — The Script is God. Only Owner / Admin can upload.
    // Contributors and Viewers see a calm "waiting for script"
    // message in the same surface — same layout, no CTA, no jargon.
    // Iter-157E P0(4) — fail-safe gating. Previously `role == null`
    // fell through to "canUpload = true", which meant a transient
    // page-load before the project role had resolved could surface
    // the Upload CTA to a Contributor. Per the strict pilot
    // requirement ("Contributors and Viewers must NEVER be invited
    // to upload scripts"), an unknown role now hides the CTA and
    // surfaces the calm waiting-state message instead. Once the
    // role arrives we re-render with the correct gate.
    // Iter-158.13.2.20f.1 (#4) — when `canManageScript` is provided
    // by the caller (Route 2 mounts have `project.can_manage_script`
    // in the Hub GET), it is the authoritative source. Legacy
    // callers that don't pass the prop fall through to the role
    // check unchanged.
    const isBetaScope = routeBase && routeBase.startsWith("/beta");
    // Iter-158.13.2.20f.3 #1 — H&M NEVER offers its own Upload
    // Script control. Even a Production Owner reaching H&M without a
    // canonical script must be routed to Production Script Setup, so
    // "status may surface sideways; authority does not". Legacy
    // (`routeBase="/projects"`) unchanged: Upload CTA still lives in
    // Script Setup on the legacy mount.
    const isHmDeptScope = routeBase === "/beta/hm-app/projects";
    const canUpload = isHmDeptScope
        ? false
        : (canManageScript !== null
            ? canManageScript === true
            : (role === "owner" || role === "admin"));

    // Iter-158.13.2.20f.3 #1 — Route-across authority: on the H&M
    // mount, if the caller has Production script authority we still
    // want them to reach Production Script Setup — the CTA just
    // changes wording (no Upload here). If they have no Production
    // authority, they see a calm "ask an authorised Production user"
    // message with no CTA.
    const canRouteToProductionScriptSetup = isHmDeptScope
        && (canManageScript === true
            || (canManageScript === null && (role === "owner" || role === "admin")));

    const goToSetup = () => {
        // Iter-158.13.2.20e.1 — canonical Script authority is
        // Production-scoped. When inside the H&M workspace
        // (`routeBase="/beta/hm-app/projects"`), the script-setup
        // surface lives at the Production route, NOT inside the
        // dept. Legacy (`routeBase="/projects"`) still uses the
        // local script-setup page unchanged.
        const target = routeBase && routeBase.startsWith("/beta")
            ? `/beta/projects/${projectId}/script-setup`
            : `${routeBase}/${projectId}/script-setup`;
        navigate(target, {
            state: {
                returnTo: { path: location.pathname, label: "Back" },
            },
        });
    };

    return (
        <section
            data-testid={copy.testid}
            data-variant={variant}
            className={`rounded-2xl border border-primary/30 bg-primary/5 p-5 ${className}`}
        >
            <div className="flex items-start gap-3">
                <div className="shrink-0 inline-flex w-10 h-10 rounded-full bg-primary/15 text-primary items-center justify-center">
                    <FileText size={18} />
                </div>
                <div className="min-w-0 flex-1">
                    <h3
                        data-testid={`${copy.testid}-title`}
                        className="font-display text-lg font-semibold text-foreground"
                    >
                        {copy.title}
                    </h3>
                    <p
                        data-testid={`${copy.testid}-body`}
                        className="mt-1 text-sm text-muted-foreground leading-relaxed"
                    >
                        {copy.body}
                    </p>
                </div>
            </div>

            {copy.steps ? (
                <ol
                    data-testid={`${copy.testid}-steps`}
                    className="mt-4 space-y-2"
                >
                    {copy.steps.map((s, idx) => {
                        const Icon = s.icon;
                        const isPrimary = s.tone === "primary";
                        return (
                            <li
                                key={s.label}
                                data-testid={`${copy.testid}-step-${idx + 1}`}
                                className={`flex items-center gap-3 rounded-lg border px-3 py-2 ${
                                    isPrimary
                                        ? "border-primary/40 bg-primary/10 text-foreground"
                                        : "border-white/10 bg-card text-muted-foreground"
                                }`}
                            >
                                <span
                                    className={`shrink-0 inline-flex w-6 h-6 rounded-full items-center justify-center font-bold text-[11px] ${
                                        isPrimary
                                            ? "bg-primary/25 text-primary"
                                            : "bg-white/10 text-muted-foreground"
                                    }`}
                                >
                                    {idx + 1}
                                </span>
                                <Icon
                                    size={14}
                                    className={isPrimary ? "text-primary" : "text-muted-foreground"}
                                />
                                <span className="text-sm font-medium">{s.label}</span>
                            </li>
                        );
                    })}
                </ol>
            ) : null}

            <div className="mt-5 flex flex-col sm:flex-row gap-2">
                {canUpload ? (
                    <button
                        type="button"
                        data-testid={`${copy.testid}-upload-cta`}
                        onClick={goToSetup}
                        className="tap min-tap flex-1 rounded-lg bg-primary hover:bg-primary/90 px-4 py-3 text-sm font-semibold text-primary-foreground ring-2 ring-primary/40 ring-offset-2 ring-offset-background flex items-center justify-center gap-2"
                    >
                        <Upload size={16} />
                        Upload Script
                    </button>
                ) : canRouteToProductionScriptSetup ? (
                    /* Iter-158.13.2.20f.3 #1 — H&M with Production
                       script authority: route to Production Script
                       Setup instead of showing an H&M Upload CTA.
                       Uses the same target URL but different label +
                       icon so the user understands they're leaving
                       H&M to configure the canonical Script.
                       Iter-158.13.2.20f.7 (BLOCKER 2) — when the
                       caller opted into the H&M-shaped missing-script
                       variant, prepend a small hint explaining the
                       cross-authority route so it doesn't read as an
                       H&M upload workflow. */
                    <div className="flex-1 flex flex-col gap-2">
                        {variant === "hm-missing-script" ? (
                            <p
                                data-testid={`${copy.testid}-authority-hint`}
                                className="text-[12px] text-muted-foreground leading-relaxed"
                            >
                                Because you also hold Production authority, you can add it now in Production Script Setup — the upload lives there, not in Hair &amp; Makeup.
                            </p>
                        ) : null}
                        <button
                            type="button"
                            data-testid={`${copy.testid}-production-setup-cta`}
                            onClick={goToSetup}
                            className="tap min-tap w-full rounded-lg bg-primary hover:bg-primary/90 px-4 py-3 text-sm font-semibold text-primary-foreground ring-2 ring-primary/40 ring-offset-2 ring-offset-background flex items-center justify-center gap-2"
                        >
                            <FileText size={16} />
                            Open Production Script Setup
                        </button>
                    </div>
                ) : isHmDeptScope && variant !== "hm-missing-script" ? (
                    /* Iter-158.13.2.20f.3 #1 — H&M-only user: no
                       Upload, no route. Calm "ask a Production user"
                       message. Status surfaces sideways; authority
                       does not.
                       Iter-158.13.2.20f.7 — when the caller opted
                       into the H&M-shaped missing-script variant,
                       the variant body already carries the same
                       message; skip this duplicate. */
                    <div
                        data-testid={`${copy.testid}-hm-waiting-message`}
                        className="flex-1 rounded-lg border border-white/10 bg-card px-4 py-3 text-sm text-muted-foreground"
                    >
                        Hair &amp; Makeup needs the Production&rsquo;s canonical script before scenes and cast will appear here. Ask an authorised Production user to upload it in Production Script Setup.
                    </div>
                ) : !isHmDeptScope ? (
                    /* Iter-157D — read-only message for contributors /
                       viewers on the legacy /projects mount. Same
                       shape, calm copy, no CTA. */
                    <div
                        data-testid={`${copy.testid}-waiting-message`}
                        className="flex-1 rounded-lg border border-white/10 bg-card px-4 py-3 text-sm text-muted-foreground"
                    >
                        Waiting for an Owner or Administrator to upload the
                        script. You&rsquo;ll see scenes and cast appear here as
                        soon as they do.
                    </div>
                ) : null}
                {secondaryAction ? (
                    <button
                        type="button"
                        data-testid={`${copy.testid}-secondary`}
                        onClick={secondaryAction.onClick}
                        className="tap min-tap flex-1 rounded-lg border border-white/10 bg-card hover:bg-white/5 px-4 py-3 text-sm font-medium text-foreground"
                    >
                        {secondaryAction.label}
                    </button>
                ) : null}
            </div>
        </section>
    );
}
